/**
 * Restaurant words: a restaurant sells "plats" sorted in "rubriques", not "produits" in "catégories", and a dish
 * is "épuisé aujourd'hui", not "en rupture de stock". The data does not change, only the labels, and only for a
 * RESTAURANT company.
 *
 * Word rules are applied to the catalog namespaces below (and to a few keys elsewhere). Everything else keeps its
 * words on purpose: a Meta or Odoo "catalogue produits", a ticket or message "catégorie" are not the menu.
 */

type Translations = { [key: string]: unknown };
type WordRule = [RegExp, string];

/** Namespaces where every label talks about the merchant's own menu. */
const SCOPES = ["PRODUCT", "CATEGORY", "ORDER", "SALE_POINT", "CATALOG_DISCOUNT", "PRODUCT_WEIGHT"];

/** Keys outside {@link SCOPES} that also talk about the menu. */
const EXTRA_KEYS = [
  "MENU.PRODUCT",
  "MENU.PRODUCTS",
  "MENU.PRODUCT_LIST",
  "METRICS_MERCHANT.LINK.PRODUCT",
  "METRICS_MERCHANT.LINK.PRODUCTS",
  "ANALYTICS.PAGE.PRODUCTS",
  "ANALYTICS.PAGE.PRODUCTS_BY_ORDERS",
  "ANALYTICS.PAGE.REVENUE_BY_PRODUCT",
  "COMPANY.PREVIEW.CATEGORIES",
  "COMPANY.PREVIEW.NOTE_HOME",
];

/** Keys inside {@link SCOPES} that are not about the menu (a discount "catégorie" is a kind of discount). */
const SKIPPED_KEYS = ["CATALOG_DISCOUNT.FILTER_EMPTY"];

/** Labels inside {@link SCOPES} naming an outside system keep their words ("ID du produit Meta"). */
const OUTSIDE_SYSTEM = /\b(Meta|Odoo|ERP|WooCommerce|Shopify|PrestaShop|CRM|SKU|API)\b/;

const WORDS: { [lang: string]: WordRule[] } = {
  fr: [
    // "s'est produit" is the verb, never the noun.
    [/(?<!s'est )\bProduits\b/g, "Plats"],
    [/(?<!s'est )\bproduits\b/g, "plats"],
    [/(?<!s'est )\bProduit\b/g, "Plat"],
    [/(?<!s'est )\bproduit\b/g, "plat"],
    // "catégorie" and "rubrique" are both feminine: articles and adjectives stay right.
    [/\bCatégories\b/g, "Rubriques"],
    [/\bcatégories\b/g, "rubriques"],
    [/\bCatégorie\b/g, "Rubrique"],
    [/\bcatégorie\b/g, "rubrique"],
  ],
  en: [
    [/\bproduct\(s\)/g, "dish(es)"],
    [/\bProducts\b/g, "Dishes"],
    [/\bproducts\b/g, "dishes"],
    [/\bProduct\b/g, "Dish"],
    [/\bproduct\b/g, "dish"],
    [/\bCategories\b/g, "Sections"],
    [/\bcategories\b/g, "sections"],
    [/\bCategory\b/g, "Section"],
    [/\bcategory\b/g, "section"],
  ],
  ar: [
    // منتجات (plural) → أطباق, منتج → طبق; prefixes (ال، و، لل) and suffixes (ك، ها) stay as they are.
    [/منتج\(ات\)/g, "أطباق"],
    [/منتجات/g, "أطباق"],
    [/منتج(?!ع)/g, "طبق"],
  ],
};

/** Whole labels: stock words become the restaurant's words, and the menu gets its own titles. */
const LABELS: { [lang: string]: { [key: string]: string } } = {
  fr: {
    "MENU.PRODUCTS": "Menu",
    "MENU.PRODUCT_LIST": "Plats",
    "CATEGORY.TITLE": "Rubriques du menu",
    "CATEGORY.HUB_TITLE": "Rubriques du menu",
    "PRODUCT.TABLE.IN_STOCK": "Disponible",
    "PRODUCT.STATUS.STOCK_LABEL": "Au menu",
    "PRODUCT.FORM.SECTIONS.PRICING": "Prix",
    "PRODUCT.STATUS.OUT_OF_STOCK": "Épuisé aujourd'hui",
    "PRODUCT.TOOLTIP.IN_STOCK": "Marquer comme disponible",
    "PRODUCT.TOOLTIP.OUT_OF_STOCK": "Marquer comme épuisé aujourd'hui",
    "SALE_POINT.STOCK.IN_STOCK": "Disponible",
    "SALE_POINT.STOCK.OUT_OF_STOCK": "Épuisé",
    "SALE_POINT.STOCK.IN_STOCK_HERE": "Disponible ici",
    "SALE_POINT.STOCK.ROW_LABEL": "{{name}} : disponible",
    "SALE_POINT.STOCK.GLOBAL_OFF": "Plat épuisé (partout)",
    "SALE_POINT.STOCK.HINT": "Désactivez un point de vente pour y afficher ce plat comme épuisé.",
    "SALE_POINT.MENU.STOCK_SUMMARY": "Épuisés ici : {{count}} plat(s)",
    "ORDER.FORM.PRODUCT_OUT_OF_STOCK": "Ce plat est épuisé aujourd'hui",
    "ONBOARDING_PAGE.SUBTITLE": "Mon restaurant en 5 étapes : vos plats, la carte, la livraison, les tables et une commande test.",
    "PRODUCT.PAGE.EMPTY_MESSAGE": "Ajoutez votre premier plat : un nom, une photo et un prix. Ex. Burger maison · 65 DH.",
    "CATEGORY.PAGE.EMPTY_DESCRIPTION": "Rangez vos plats comme sur votre carte : Burgers, Pizzas, Sushis, Boissons… Vous choisissez vos rubriques.",
    "SALE_POINT.EMPTY_MESSAGE": "Ajoutez votre restaurant et placez-le sur la carte : la livraison et le retrait partent de là.",
    "CATALOG_DISCOUNT.EMPTY_TEXT": "Créez votre première promotion : -20 % sur les pizzas le mardi, un code comme MIDI10 ou la livraison offerte.",
    "ORDER_KANBAN.COL_PENDING": "Nouvelles",
    "ORDER_KANBAN.COL_PREPARING": "En cuisine",
    "ORDER_KANBAN.COL_READY": "Prêtes",
    "ORDER_KANBAN.COL_PICKED_UP": "Parties",
    "ORDER_KANBAN.COL_DELIVERED": "Servies · Livrées",
  },
  en: {
    "MENU.PRODUCTS": "Menu",
    "MENU.PRODUCT_LIST": "Dishes",
    "CATEGORY.TITLE": "Menu sections",
    "CATEGORY.HUB_TITLE": "Menu sections",
    "PRODUCT.TABLE.IN_STOCK": "Available",
    "PRODUCT.STATUS.STOCK_LABEL": "On the menu",
    "PRODUCT.FORM.SECTIONS.PRICING": "Price",
    "PRODUCT.STATUS.OUT_OF_STOCK": "Sold out today",
    "PRODUCT.TOOLTIP.IN_STOCK": "Mark as available",
    "PRODUCT.TOOLTIP.OUT_OF_STOCK": "Mark as sold out today",
    "SALE_POINT.STOCK.IN_STOCK": "Available",
    "SALE_POINT.STOCK.OUT_OF_STOCK": "Sold out",
    "SALE_POINT.STOCK.IN_STOCK_HERE": "Available here",
    "SALE_POINT.STOCK.ROW_LABEL": "{{name}}: available",
    "SALE_POINT.STOCK.GLOBAL_OFF": "Dish sold out (everywhere)",
    "SALE_POINT.STOCK.HINT": "Turn a store off to show this dish as sold out there.",
    "SALE_POINT.MENU.STOCK_SUMMARY": "Sold out here: {{count}} dish(es)",
    "ORDER.FORM.PRODUCT_OUT_OF_STOCK": "This dish is sold out today",
    "ONBOARDING_PAGE.SUBTITLE": "My restaurant in 5 steps: your dishes, the map, delivery, tables and a test order.",
    "PRODUCT.PAGE.EMPTY_MESSAGE": "Add your first dish: a name, a photo and a price. E.g. Homemade burger · 65 MAD.",
    "CATEGORY.PAGE.EMPTY_DESCRIPTION": "Sort your dishes like on your menu: Burgers, Pizzas, Sushi, Drinks… You choose your sections.",
    "SALE_POINT.EMPTY_MESSAGE": "Add your restaurant and put it on the map: delivery and pickup start there.",
    "CATALOG_DISCOUNT.EMPTY_TEXT": "Create your first promotion: -20% on pizzas on Tuesdays, a code like LUNCH10 or free delivery.",
    "ORDER_KANBAN.COL_PENDING": "New",
    "ORDER_KANBAN.COL_PREPARING": "In the kitchen",
    "ORDER_KANBAN.COL_READY": "Ready",
    "ORDER_KANBAN.COL_PICKED_UP": "On the way",
    "ORDER_KANBAN.COL_DELIVERED": "Served · Delivered",
  },
  ar: {
    "MENU.PRODUCTS": "القائمة",
    "MENU.PRODUCT_LIST": "الأطباق",
    "CATEGORY.TITLE": "أقسام القائمة",
    "CATEGORY.HUB_TITLE": "أقسام القائمة",
    "PRODUCT.STATUS.OUT_OF_STOCK": "نفد اليوم",
    "PRODUCT.STATUS.STOCK_LABEL": "في القائمة",
    "PRODUCT.FORM.SECTIONS.PRICING": "السعر",
    "PRODUCT.TOOLTIP.IN_STOCK": "وضع علامة كمتوفر",
    "PRODUCT.TOOLTIP.OUT_OF_STOCK": "وضع علامة كنافد اليوم",
    "SALE_POINT.STOCK.GLOBAL_OFF": "الطبق نافد (في كل مكان)",
    "ORDER.FORM.PRODUCT_OUT_OF_STOCK": "هذا الطبق نفد اليوم",
    "ONBOARDING_PAGE.SUBTITLE": "مطعمي في 5 خطوات: أطباقكم، الخريطة، التوصيل، الطاولات وطلب تجريبي.",
    "PRODUCT.PAGE.EMPTY_MESSAGE": "أضيفوا طبقكم الأول: الاسم والصورة والسعر. مثال: برغر منزلي · 65 درهم.",
    "CATEGORY.PAGE.EMPTY_DESCRIPTION": "رتّبوا أطباقكم كما في قائمتكم: برغر، بيتزا، سوشي، مشروبات… أنتم تختارون الأقسام.",
    "SALE_POINT.EMPTY_MESSAGE": "أضيفوا مطعمكم وضعوه على الخريطة: منه يبدأ التوصيل والاستلام.",
    "ORDER_KANBAN.COL_PENDING": "جديدة",
    "ORDER_KANBAN.COL_PREPARING": "في المطبخ",
    "ORDER_KANBAN.COL_READY": "جاهزة",
    "ORDER_KANBAN.COL_PICKED_UP": "في الطريق",
    "ORDER_KANBAN.COL_DELIVERED": "قُدّمت · وُصّلت",
  },
};

/** Changes for one language: key path ("PRODUCT.TABLE.IN_STOCK") → restaurant label. Unknown keys are left out. */
export function restaurantLabelChanges(translations: Translations, lang: string): Map<string, string> {
  const changes = new Map<string, string>();
  const rules = WORDS[lang] ?? [];
  const visit = (node: unknown, path: string): void => {
    if (typeof node === "string") {
      if (!isMenuKey(path) || OUTSIDE_SYSTEM.test(node)) {
        return;
      }
      const next = rules.reduce((text, [pattern, word]) => text.replace(pattern, word), node);
      if (next !== node) {
        changes.set(path, next);
      }
    } else if (node && typeof node === "object") {
      for (const [key, child] of Object.entries(node as Translations)) {
        visit(child, path ? `${path}.${key}` : key);
      }
    }
  };
  visit(translations, "");
  for (const [path, label] of Object.entries(LABELS[lang] ?? {})) {
    if (typeof valueAt(translations, path) === "string") {
      changes.set(path, label);
    }
  }
  return changes;
}

/** A copy of the translations with the restaurant labels; the given object is not changed. */
export function withRestaurantWords(translations: Translations, lang: string): Translations {
  const changes = restaurantLabelChanges(translations, lang);
  if (!changes.size) {
    return translations;
  }
  const copy = structuredClone(translations);
  for (const [path, label] of changes) {
    const keys = path.split(".");
    const last = keys.pop() as string;
    const parent = keys.reduce((node, key) => node[key] as Translations, copy);
    parent[last] = label;
  }
  return copy;
}

function isMenuKey(path: string): boolean {
  if (SKIPPED_KEYS.includes(path)) {
    return false;
  }
  return SCOPES.includes(path.split(".")[0]) || EXTRA_KEYS.includes(path);
}

function valueAt(translations: Translations, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>((node, key) => (node && typeof node === "object" ? (node as Translations)[key] : undefined), translations);
}
